/* REACT */
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useLayoutEffect,
} from 'react'

/* 3RD PARTIES */
import { connect } from 'react-redux'
import cloneDeep from 'lodash.clonedeep'
import get from 'lodash.get'
import {
  Maximize,
  Minus,
  RefreshCw,
  SendHorizonal,
  Share,
  MoreVertical,
  MoreHorizontal,
  Mail,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../../components/ui'

/* COMPONENTS */
import MessageComponent from './MessageComponent'
import Typing from './Typing'
import { Image, Button } from '../../../../components/ui'
import FeedbackModal from '../../../../components/modals/feedback/FeedbackModal'
import SaveChatModal from '../../../../components/modals/saveChat/SaveChatModal'
import API from '../../../../utils/api'
import {
  addUserMessageToSend,
  setRegenerateResponse,
  requestPlan,
  setIsGridMode,
  refreshChat,
  updateLastActiveTime,
} from '../../../../store/maya/actions'
import { getGrids } from '../../../../store/maya/selectors'
import {
  setIsPopoverChatOpen,
  setShowSendChatModal,
} from '../../../../store/general/actions'
import { DarkOverlay } from '../../../../components/modals/saveChat/style'

/* STYLES */
import {
  MayaInputContainer,
  ChatSectionScrollable,
  MayaMessageInput,
  MayaMessageInputSubmitButton,
  MayaQuickRepliesConatiner,
  MayaQuickRepliesBullet,
  RegenerateResponse,
  MayaInputContainerWrapper,
  MayaInputBox,
  ChatSectionCtaWrapper,
  ChatSectionCtaActionsWrapper,
  PopoverChatIconsWrapper,
  PopoverChatIconWrapper,
  PopoverChatIconsRightSectionWrapper,
  PopoverChatIconsLeftSectionWrapper,
  PoweredByMayaWrapper,
  PoweredByMayaLink,
  PoweredByMayaText,
  MessageAvatarContainer,
  BotNameWrapper,
  StatusDot,
  MayaMessageInputWrapper,
  NotificationContainer,
} from './style.js'

/* UTILS/CONSTANTS */
import {
  CLOUDFRONT_BASE_URL,
  CLIENT_STYLINGS,
} from '../../../../utils/constants'
import { Analytics, getTranslations } from '../../../../utils'

const ChatSection = ({
  isPlanRequested,
  isChatReady,
  isLessThanLaptop,
  isMobile,
  slug,
  token,
  messages,
  isMayaResponding,
  quickReplies,
  regenerateResponse,
  setRegenerateResponse,
  defaultMessages,
  language,
  intent,
  grids,
  organizationSlug,
  setIsGridMode,
  isSocketConnectionEstablished,
  setIsPopoverChatOpen,
  organizationDetails,
  updateLastActiveTime,
  showFeedbackModal,
  isOpen,
  showSendChatModal,
  setShowSendChatModal,
}) => {
  const SubmitText = `${CLOUDFRONT_BASE_URL}/assets/${organizationSlug}/images/MayaSendTextButton.png`
  const MayaAvatar = CLIENT_STYLINGS[organizationSlug].MAYA_AVATAR_URL

  const [prompt, setPrompt] = useState('')
  const [currentMessagesLength, setCurrentMessagesLength] = useState(0)
  const [isTextareaFocused, setIsTextareaFocused] = useState(false)
  const stopScrollDown = useRef(false)
  const assistantMsgTop = useRef(null)
  const textareaRef = useRef(null)
  // use global showSaveChatModal via Redux

  // Handle textarea auto-growing
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = '40px'
      const scrollHeight = textarea.scrollHeight
      textarea.style.height = `${Math.min(scrollHeight, 200)}px` // Set new height with max limit
    }
  }, [prompt])

  // Add effect to scroll when feedback modal appears
  useEffect(() => {
    if (showFeedbackModal) {
      // Allow auto scrolling when feedback modal appears
      stopScrollDown.current = false
      executeScroll()
    }
  }, [showFeedbackModal, isMayaResponding])

  // GET NUMBER OF LINES IN TEXT MESSAGE
  const getNumberOfLines = div => {
    // Get the computed styles for the div
    const computedStyle = window.getComputedStyle(div)

    // Get the height of the div
    const divHeight = div.clientHeight

    // Get the line-height of the div
    const lineHeight = parseFloat(computedStyle.lineHeight)

    // Calculate the number of lines
    const numberOfLines = Math.round(divHeight / lineHeight)

    return numberOfLines
  }

  const executeScroll = () => {
    const element = document.getElementById('chat-section-scrollable-popover')
    element.scrollTop = element.scrollHeight
  }

  useEffect(() => {
    const scrollableDiv = document.getElementById(
      'chat-section-scrollable-popover'
    )

    const handleScroll = () => {
      const lastAssistantMsgDiv = document.getElementById(`assistant-0`)

      if (lastAssistantMsgDiv) {
        const lastAssistantMsgDivRect =
          lastAssistantMsgDiv.getBoundingClientRect()

        // USER HAS SCROLLED UP
        if (
          lastAssistantMsgDivRect.top > assistantMsgTop.current &&
          Math.abs(lastAssistantMsgDivRect.top - assistantMsgTop.current) > 40
        ) {
          stopScrollDown.current = true
        } else {
          assistantMsgTop.current = lastAssistantMsgDivRect.top
        }
      }
    }

    scrollableDiv.addEventListener('scroll', handleScroll)

    // Cleanup function to remove the event listener
    return () => {
      scrollableDiv.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    // SCROLL TO BOTTOM WHENEVER NEW MESSAGE ADDED
    if (messages?.length > currentMessagesLength) {
      setCurrentMessagesLength(messages?.length)

      // ALLOW AUTO SCROLLING DOWN ON EVERY NEW MESSAGE
      stopScrollDown.current = false

      // SCROLL TO THE BOTTOM
      executeScroll()

      const lastAssistantMsgDiv = document.getElementById(`assistant-0`)

      // SET AGAIN THE TOP POSITION OF FIRST ASSISTANT MESSAGE
      if (lastAssistantMsgDiv) {
        const lastAssistantMsgDivRect =
          lastAssistantMsgDiv.getBoundingClientRect()
        assistantMsgTop.current = lastAssistantMsgDivRect.top
      }
    }

    if (stopScrollDown.current) {
      // USER HAS SCROLLED UP SO DON'T AUTO SCROLL
    } else {
      // USER HASN'T SCROLLED UP SO SCROLL TO BOTTOM
      executeScroll()
    }
  }, [messages])

  useLayoutEffect(() => {
    if (isOpen) {
      stopScrollDown.current = false
      executeScroll()
    }
  }, [isOpen])

  const onRequestPlanClick = () => {
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'cta',
      message_subtype: 'leave_details',
    })

    requestPlan({ slug, token })
    setIsGridMode(false)
  }

  const regenerateMayaResponse = () => {
    let lastUserMessage = ''
    for (let message of messages) {
      if (message?.role === 'user') {
        lastUserMessage = message?.content || ''
      }
    }
    addUserMessageToSend({ message: lastUserMessage })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'user',
    })
    setRegenerateResponse(false)
  }

  const handleQuickReplyClick = (event, reply) => {
    event.preventDefault()
    const message = { role: 'user', content: reply }
    addUserMessageToSend({ message: reply })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'quick_reply',
    })
    setPrompt('')

    setTimeout(() => {
      executeScroll()
    }, 100)
  }

  const handleClick = e => {
    if (!isChatReady || isMayaResponding) return

    e.preventDefault()
    if (!prompt) return // If user hasn't typed anything, don't send message

    addUserMessageToSend({ message: cloneDeep(prompt) })
    Analytics.getInstance().sendEvent('maya_sent_message', {
      message_type: 'user',
    })
    setPrompt('')

    setTimeout(() => {
      executeScroll()
    }, 100)
  }

  const handleKeyPress = e => {
    if (
      e.keyCode === 13 &&
      !e.shiftKey &&
      !isMayaResponding &&
      isSocketConnectionEstablished
    )
      handleClick(e)
  }

  const updateUserPromp = e => {
    // UPDATE LAST ACTIVE TIME
    // WHEN USER BECOMES IDLE WE SHOW CUSTOMER FEEDBACK MODAL
    updateLastActiveTime()

    setPrompt(e.target.value)
  }

  const handleTextareaFocus = () => {
    setIsTextareaFocused(true)
  }

  const handleTextareaBlur = () => {
    setIsTextareaFocused(false)
  }

  const showPoweredByMaya = () => {
    // Find the first element with a non-null showPoweredByMayaButton value
    const poweredByMayaElement = organizationDetails?.configs?.find(
      element => element.showPoweredByMayaButton !== null
    )

    // Get the showPoweredByMayaButton value if found
    const canShowPoweredByMaya = poweredByMayaElement
      ? poweredByMayaElement.showPoweredByMayaButton
      : null

    return canShowPoweredByMaya
  }

  const handleChatRefresh = () => {
    refreshChat()

    const url = new URL(window.location.href)

    // Remove the slug and token parameters from the url
    url.searchParams.delete('slug')
    url.searchParams.delete('token')
    url.searchParams.delete('amp;token')

    // Update the URL without refreshing the page
    window.history.replaceState(null, '', url.toString())

    // Refresh the page
    window.location.reload()
  }

  const aIGeneratedQuickReplyOptions = useMemo(() => {
    const lastMessage = messages[messages.length - 1]
    const quickReplyOptions = lastMessage?.quickReplyOptions

    return quickReplyOptions?.length ? quickReplyOptions : null
  }, [messages])

  const recommendataionsCtaUrl = useMemo(() => {
    if (!intent) return null

    let url = get(
      intent,
      `response.${intent.type}.meta_data.recommendations_cta_url`,
      null
    )

    return url
  }, [intent])

  const containsUserMessage = messages?.some(
    message => message?.type === 'user'
  )

  const isBrandWebsite = useMemo(() => {
    try {
      const chatUrl = organizationDetails?.configs[0]?.chatUrl

      // EXTRACT THE DOMAIN FROM THE CHAT URL
      const getRootDomain = hostname => {
        if (!hostname) return null
        const parts = hostname.split('.')
        if (parts.length <= 2) return hostname
        return parts.slice(-2).join('.')
      }

      // CHECK IF CURRENT DOMAIN IS THE SAME AS THE DOMAIN IN THE CHAT URL
      const currentDomain = getRootDomain(window.location.hostname)

      const domain = chatUrl
        ? getRootDomain(
            new URL(
              chatUrl.startsWith('http') || chatUrl.startsWith('https')
                ? chatUrl
                : `https://${chatUrl}`
            ).hostname
          )
        : null

      if (domain.includes('livetheworld.com')) {
        // E.G. for neckermann the chat url is ours own domain but its not a brand website
        return false
      }

      // CHECK IF THE CURRENT DOMAIN IS THE SAME AS THE DOMAIN IN THE CHAT URL
      // IF SAME DOMAIN THEN ITS THE ACTUAL CLIENT WEBSITE
      // IF DIFFERENT DOMAIN THEN ITS A BRAND WEBSITE
      if (currentDomain !== domain) {
        return true
      }

      return false
    } catch (error) {
      console.log('Err', error.message)
      return false
    }
  }, [organizationDetails])

  return (
    <div
      style={{
        height: '100%',
        position: 'relative',
      }}
    >
      <PopoverChatIconsWrapper>
        <PopoverChatIconsLeftSectionWrapper>
          <MessageAvatarContainer>
            <Image
              src={MayaAvatar}
              alt='Message Avatar'
              width={'34px'}
              height={'34px'}
              minWidth={'34px'}
              minHeight={'34px'}
              objectFit={'contain'}
            />
          </MessageAvatarContainer>

          <BotNameWrapper>
            {organizationDetails?.configs?.length &&
            organizationDetails?.configs[0]?.botName
              ? organizationDetails?.configs[0]?.botName
              : 'Maya'}
          </BotNameWrapper>
          <StatusDot />
        </PopoverChatIconsLeftSectionWrapper>
        <PopoverChatIconsRightSectionWrapper style={{ alignItems: 'flex-end' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              padding: '0px 6px',
            }}
          >
            {showPoweredByMaya() && (
              <PoweredByMayaWrapper>
                <PoweredByMayaText>Powered by</PoweredByMayaText>{' '}
                <PoweredByMayaLink
                  href={'https://www.mayatravel.ai/'}
                  target={'_blank'}
                >
                  Maya
                </PoweredByMayaLink>
              </PoweredByMayaWrapper>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <PopoverChatIconWrapper as='button' type='button'>
                  <MoreHorizontal size={16} strokeWidth={2.2} />
                </PopoverChatIconWrapper>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onSelect={() => {
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'restart',
                    })
                    handleChatRefresh()
                  }}
                >
                  <RefreshCw size={14} />{' '}
                  {getTranslations('refresh') || 'Refresh'}
                </DropdownMenuItem>
                {organizationDetails?.configs?.length &&
                  organizationDetails?.configs[0]?.chatUrl &&
                  organizationDetails?.configs[0]?.enableFullPageUrlCta &&
                  !isBrandWebsite && (
                    <DropdownMenuItem
                      onSelect={() => {
                        setIsGridMode(true)
                        Analytics.getInstance().sendEvent('maya_click_button', {
                          button_type: 'cta',
                          button_text: `${getTranslations(
                            'see_recommendations_cta_title'
                          )}`,
                        })
                        window.location.href =
                          organizationDetails?.configs?.[0]?.chatUrl
                      }}
                    >
                      <Maximize size={14} />{' '}
                      {getTranslations('full_screen') || 'Full Screen'}
                    </DropdownMenuItem>
                  )}
                <DropdownMenuItem
                  onSelect={() => {
                    setShowSendChatModal(true)
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'popover_icon',
                      button_subtype: 'send_chat',
                    })
                  }}
                >
                  <Mail size={14} />{' '}
                  {getTranslations('send_chat') || 'Send chat'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <PopoverChatIconWrapper
              as='button'
              type='button'
              onClick={() => {
                setIsPopoverChatOpen(false)
                Analytics.getInstance().sendEvent('maya_click_button', {
                  button_type: 'popover_icon',
                  button_subtype: 'close_popover',
                })
              }}
            >
              <Minus size={18} strokeWidth={2.5} />
            </PopoverChatIconWrapper>
          </div>
        </PopoverChatIconsRightSectionWrapper>
      </PopoverChatIconsWrapper>

      <ChatSectionScrollable
        id='chat-section-scrollable-popover'
        $isMobile={isMobile}
        $isTextareaFocused={isTextareaFocused}
      >
        {messages &&
          messages?.map((message, i) => {
            return (
              <>
                {message?.content ? (
                  // ONLY SHOW IF CONTENT IS THERE
                  <MessageComponent
                    language={language}
                    content={message?.content}
                    message={message}
                    disclaimer={message?.disclaimer}
                    sender={message?.role === 'user'}
                    key={`${message?.content} - ${i}`}
                    id={`${message?.role}-${i}`}
                    showFeedbackOption={
                      message?.role === 'assistant' &&
                      messages?.length > defaultMessages?.length &&
                      ((i < messages?.length - 1 &&
                        (message?.feedback === 0 || message?.feedback === 1)) ||
                        (i === messages?.length - 1 &&
                          isChatReady &&
                          !isMayaResponding))
                    }
                    removeMarginBottom={
                      i === messages?.length - 1 &&
                      (aIGeneratedQuickReplyOptions || showFeedbackModal)
                    }
                  />
                ) : null}
                {/* Show feedback modal after the last message only in mobile view */}
                {i === messages.length - 1 &&
                message?.role === 'assistant' &&
                showFeedbackModal &&
                isLessThanLaptop ? (
                  <FeedbackModal
                    mobileView={true}
                    language={language}
                    id='feedback-modal'
                  />
                ) : null}
                {/* SaveChatModal handled at Popover level */}
              </>
            )
          })}
        {/* SHOW LOADER UNTIL MAYA CONTENT STARTS APPEARING */}
        {isMayaResponding && !messages[messages.length - 1].content && (
          <Typing />
        )}
        {regenerateResponse && (
          <RegenerateResponse onClick={regenerateMayaResponse}>
            {getTranslations('regenerate_response')}
          </RegenerateResponse>
        )}

        {/* SHOW QUICK REPLY BUTTONS ONLY INITIALLY */}
        {isChatReady && messages?.length <= defaultMessages?.length ? (
          <MayaQuickRepliesConatiner>
            {quickReplies &&
              quickReplies?.map(reply => (
                <Button
                  size='sm'
                  variant='outline'
                  key={reply?.content || ``}
                  onClick={e => {
                    handleQuickReplyClick(e, reply?.content || ``)
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'quick_reply',
                      button_text: reply?.content || '',
                    })
                  }}
                >
                  {reply?.content || ``}
                </Button>
              ))}
          </MayaQuickRepliesConatiner>
        ) : null}

        {/* SHOW AI GENERATED QUICK REPLY OPTIONS */}
        {aIGeneratedQuickReplyOptions ? (
          <MayaQuickRepliesConatiner id='maya-ai-quick-replies-container'>
            {aIGeneratedQuickReplyOptions &&
              aIGeneratedQuickReplyOptions?.map(reply => (
                <MayaQuickRepliesBullet
                  key={reply || ``}
                  onClick={e => {
                    handleQuickReplyClick(e, reply || ``)
                    Analytics.getInstance().sendEvent('maya_click_button', {
                      button_type: 'quick_reply_ai',
                      button_text: reply || '',
                    })
                  }}
                >
                  {reply || ``}
                </MayaQuickRepliesBullet>
              ))}
          </MayaQuickRepliesConatiner>
        ) : null}
        <div
          style={{
            paddingTop: 57,
          }}
        ></div>
      </ChatSectionScrollable>
      <MayaInputBox id='maya-input-box'>
        <MayaInputContainerWrapper id='maya-input-container-wrapper'>
          <MayaInputContainer>
            {messages?.length !== defaultMessages?.length &&
              intent &&
              grids && (
                <ChatSectionCtaWrapper>
                  <ChatSectionCtaActionsWrapper
                    id='chat-section-cta-wrapper'
                    $hasButtons={
                      (organizationDetails?.configs?.length &&
                        !organizationDetails?.configs[0]
                          ?.enableFullPageUrlCta &&
                        recommendataionsCtaUrl) ||
                      (organizationDetails?.configs?.length &&
                        organizationDetails?.configs[0]?.chatUrl &&
                        organizationDetails?.configs[0]?.enableFullPageUrlCta &&
                        !isBrandWebsite) ||
                      (isChatReady &&
                        organizationDetails?.configs?.length &&
                        organizationDetails?.configs[0]
                          ?.enableRequestPlanCtaPopover)
                    }
                  >
                    {/* SHOW CUSTOM CTA URL FOR RECOMMENDATIONS */}
                    {organizationDetails?.configs?.length &&
                    !organizationDetails?.configs[0]?.enableFullPageUrlCta &&
                    !isBrandWebsite &&
                    containsUserMessage &&
                    recommendataionsCtaUrl ? (
                      <Button
                        size='sm'
                        href={recommendataionsCtaUrl}
                        onClick={() => {
                          setIsGridMode(true)
                          Analytics.getInstance().sendEvent(
                            'maya_click_button',
                            {
                              button_type: 'cta',
                              button_text: `${getTranslations(
                                'see_recommendations_cta_title'
                              )}`,
                            }
                          )
                        }}
                      >
                        {getTranslations('see_recommendations_cta_title')}
                      </Button>
                    ) : null}
                    {organizationDetails?.configs?.length &&
                    organizationDetails?.configs[0]?.chatUrl &&
                    organizationDetails?.configs[0]?.enableFullPageUrlCta &&
                    !isBrandWebsite &&
                    containsUserMessage ? (
                      <Button
                        href={organizationDetails?.configs?.[0]?.chatUrl}
                        size='sm'
                        variant='outline'
                        onClick={() => {
                          setIsGridMode(true)
                          Analytics.getInstance().sendEvent(
                            'maya_click_button',
                            {
                              button_type: 'cta',
                              button_text: `${getTranslations(
                                'see_recommendations_cta_title'
                              )}`,
                            }
                          )
                        }}
                      >
                        {getTranslations('see_recommendations_cta_title')}
                      </Button>
                    ) : null}
                    {isChatReady ? (
                      organizationDetails?.configs?.length &&
                      organizationDetails?.configs[0]
                        .enableRequestPlanCtaPopover ? (
                        organizationDetails?.configs[0]?.requestPlanUrl ? (
                          <Button
                            size='sm'
                            href={
                              organizationDetails?.configs?.[0]?.requestPlanUrl
                            }
                            onClick={() => {
                              Analytics.getInstance().sendEvent(
                                'maya_click_button',
                                {
                                  button_type: 'cta',
                                  button_text: `${getTranslations(
                                    'request_plan_cta'
                                  )}`,
                                  button_subtype: 'leave_details',
                                }
                              )
                            }}
                          >
                            {getTranslations('request_plan_cta')}
                          </Button>
                        ) : (
                          <Button
                            size='sm'
                            onClick={() => {
                              Analytics.getInstance().sendEvent(
                                'maya_click_button',
                                {
                                  button_type: 'cta',
                                  button_text: `${getTranslations(
                                    'request_plan_cta'
                                  )}`,
                                  button_subtype: 'leave_details',
                                }
                              )
                              onRequestPlanClick()
                            }}
                            disabled={
                              isPlanRequested ||
                              isMayaResponding ||
                              !isChatReady
                            }
                          >
                            {!isPlanRequested
                              ? getTranslations('request_plan_cta')
                              : getTranslations('is_plan_requested')}
                          </Button>
                        )
                      ) : null
                    ) : null}
                  </ChatSectionCtaActionsWrapper>
                </ChatSectionCtaWrapper>
              )}

            <MayaMessageInputWrapper>
              <MayaMessageInput
                ref={textareaRef}
                type='text'
                placeholder={getTranslations('chat_placeholder')}
                value={prompt}
                onChange={updateUserPromp}
                onKeyDown={handleKeyPress}
                onFocus={handleTextareaFocus}
                onBlur={handleTextareaBlur}
              />
              <MayaMessageInputSubmitButton
                onClick={handleClick}
                $disabled={
                  isMayaResponding ||
                  !isSocketConnectionEstablished ||
                  !isChatReady ||
                  !prompt.trim()
                }
              >
                <SendHorizonal size={20} strokeWidth={2.2} />
              </MayaMessageInputSubmitButton>
            </MayaMessageInputWrapper>
          </MayaInputContainer>
        </MayaInputContainerWrapper>
      </MayaInputBox>
      {/* Always render; CSS controls visibility */}
      <DarkOverlay
        $open={showSendChatModal}
        onClick={() => setShowSendChatModal(false)}
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1001,
          width: '100%',
          maxWidth: '380px',
          // Wrapper remains static; modal handles its own fade
        }}
      >
        <SaveChatModal
          isOpen={showSendChatModal}
          onClose={() => setShowSendChatModal(false)}
          onSubmit={({ email }) =>
            API.postRequest('/mail/send-chat', {
              slug,
              email,
            })
          }
        />
      </div>
    </div>
  )
}

const mapStateToProps = state => {
  return {
    isPlanRequested: state?.maya?.isPlanRequested,
    slug: state?.maya?.slug,
    token: state?.maya?.token,
    messages: state?.maya?.messages,
    isMayaResponding: state?.maya?.isMayaResponding,
    isChatReady: state?.maya?.isChatReady,
    regenerateResponse: state?.maya?.regenerateResponse,
    quickReplies: state?.maya?.quickReplies,
    defaultMessages: state?.maya?.defaultMessages,
    intent: state?.maya?.intent,
    grids: getGrids(state),
    organizationSlug: state?.general?.organizationSlug,
    isSocketConnectionEstablished: state?.maya?.isSocketConnectionEstablished,
    organizationDetails: state?.maya?.organizationDetails,
    showFeedbackModal: state?.maya?.showFeedbackModal,
    isLessThanLaptop: state?.general?.isLessThanLaptop,
    showSendChatModal: state?.general?.showSendChatModal,
  }
}

const mapDispatchToProps = dispatch => ({
  setRegenerateResponse: data => dispatch(setRegenerateResponse(data)),
  setIsGridMode: data => dispatch(setIsGridMode(data)),
  setIsPopoverChatOpen: isOpen => dispatch(setIsPopoverChatOpen(isOpen)),
  updateLastActiveTime: () => dispatch(updateLastActiveTime()),
  setShowSendChatModal: isOpen => dispatch(setShowSendChatModal(isOpen)),
})

export default connect(mapStateToProps, mapDispatchToProps)(ChatSection)
